import {
  disconnectAudioNode,
  stopMediaRecorder,
  closeAudioContext,
} from "@/store/audio-capture/audio-capture-error-handling-helpers";
import { RecordingStatus } from "@/store/audio-capture/models/recording-status";
import { logger } from "@/modules/logger";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import type { DesktopRecorderEngine, WebRecorderEngine } from "@/store/audio-capture/engines";

export class LifecycleManager {
  constructor(
    private readonly store: AppStoreAudioCaptureStore,
    private readonly desktopRecorder: DesktopRecorderEngine,
    private readonly webRecorder: WebRecorderEngine
  ) {}

  resetRecording({ preserveLockedStatus }: { preserveLockedStatus?: boolean } = {}) {
    logger.info({
      message: "[AppStoreAudioCaptureStore] resetRecording",
      info: {
        status: this.store.status,
      },
    });
    this.store.observability.endSession();
    if (!preserveLockedStatus) {
      this.store.session.completeCurrentSession();
    }

    this.store.device.clearDesktopInitializationBuffer();
    this.store.resetAutoStopState();
    void this.desktopRecorder.dispose();
    void this.webRecorder.dispose();
    stopMediaRecorder(this.store.mediaRecorder);
    this.store.mediaRecorder = null;
    this.store.ui.resetFriendlyDebugInfo();
    this.store.activeAudioInputDevices = [];
    this.store.activeAudioInputDeviceLabel = null;
    this.store.isMicrophoneDisconnected = false;
    this.store.setAudioSilenceSince(null);

    if (this.store.analyser) {
      disconnectAudioNode(this.store.analyser);
      this.store.analyser = null;
    }

    if (this.store.visualizer) {
      this.store.visualizer.dispose();
      this.store.visualizer = null;
    }

    if (this.store.audioContext) {
      closeAudioContext(this.store.audioContext);
      this.store.audioContext = null;
    }

    this.store.transcription.reset({ clearTranscript: !preserveLockedStatus });
    if (preserveLockedStatus) {
      this.store.setStatus(RecordingStatus.LOCKED);
    } else {
      this.store.setStatus(RecordingStatus.IDLE);
      this.store.restoredDuration = 0;
      this.store.activeNoteId = null;
      this.store.audioBlob = null;
      this.store.audioUrl = null;
      this.store.audioChunks = [];
    }

    this.store.session.resetDuration();
    this.store.error = null;
    this.store.session.resetTotalPausedTime();
    this.store.session.resetPausedTime();
    this.store.isRecordingWithoutTranscription = false;
    this.store.updateUploadProgress(null);

    this.store.session.clearDurationInterval();

    if (this.store.releaseRecordingLock) {
      this.store.releaseRecordingLock(false);
      this.store.releaseRecordingLock = null;
    }

    this.store.activeNoteObservable?.setIsProcessingSuggestion(false);
    this.store.ui.resetFriendlyDebugInfo();
    this.store.updateFriendlyDebugInfo();
  }

  clearRecording() {
    logger.info({
      message: "[AppStoreAudioCaptureStore] clearRecording",
      info: {
        status: this.store.status,
      },
    });
    const noteId = this.store.activeNoteId ?? this.store.currentSession?.noteId;
    const recordingId = this.store.currentSession?.recordingId;
    void this.store.persistence.clearByScope({
      noteId: noteId ?? undefined,
      recordingId,
    });
    this.resetRecording();
  }

  async clearAudioCapturesByNoteId(noteId: string) {
    const recordingId =
      this.store.currentSession?.noteId === noteId
        ? this.store.currentSession.recordingId
        : undefined;
    await this.store.persistence.clearByScope({ noteId, recordingId });
    if (this.store.activeNoteId === noteId) this.resetRecording();
  }
}
